// AUTO-GENERATED by scripts/build_sponsor_data.mjs — DO NOT EDIT BY HAND.
// Source: packages/shared/src/data/corryk_ownership_names.json
// Curated Corryk ownership reference, generated 2026-06-12.
// 308 operators with an ownership signal. Point-in-time (mid-2026);
// matching reference only — never assert PE ownership without a registry chain
// landing on one of these names. Carry confidence; treat 'low' as a lead.

export type BackerType =
  | 'pe'
  | 'infra'
  | 'sovereign'
  | 'strategic'
  | 'listed'
  | 'reit'
  | 'family'
  | 'cooperative'
  | 'foundation';

export type OwnershipConfidence = 'high' | 'medium' | 'low';

export interface SponsorOperator {
  /** Canonical platform/operator name. */
  name: string;
  /** Register-style names to match a resolved corporate_parent against. */
  aliases: string[];
  /** Named owner/sponsor; null when the type is known but the fund isn't. */
  backer: string | null;
  backerType: BackerType;
  confidence: OwnershipConfidence;
  /** Panel subsegment for display context. */
  sector: string | null;
}

export const SPONSOR_DATA_VERSION = '2026-10-20';

export const SPONSOR_OPERATORS: ReadonlyArray<SponsorOperator> = [
  // ══ GROUP-OWNERSHIP ROUND (GO), 2026-10-19/20 ══════════════════════════════
  // Controllers found by the GO research round (handover/care_homes/GROUP_OWNERSHIP_ROUND_RESULTS_2026-10-20.md;
  // quote-checked rows in data/care_homes/_ownership_pass). Every live group label is carried as an alias in
  // both its plain and 'XX: ' form (see the 2026-08-20 note below). Updated in place further down: Alloheim,
  // Ambea, Domidep, Clariane, emeis (aliases); Vitalia Home -> StepStone + Greykite (Nov 2025, three sources);
  // Anima Care -> AG Insurance (2022); Colisée -> confidence low (Dec 2025 lender-led recapitalisation lock-up,
  // EQT Infrastructure V exiting, completion not verified).
  // VERIFY rule: a PE claim resting on one source or a stale (pre-2022) source is entered as strategic/low
  // with backer null — Habitus (lead: Apposite Capital), Argentum (lead: Lafayette Mittelstand Capital),
  // Silver Stream (lead: Waterland, 2019 source).
  // ⚠️ This file is hand-maintained since 2026-08-20: scripts/build_sponsor_data.mjs regenerates ONLY from the
  // JSON and would delete both commission sections. Edit here.
  {
    name: 'Active Assistance',
    aliases: [
      'Active Assistance (Uk) Group',
      'GB: Active Assistance (Uk) Group',
      'Active Care Group',
    ],
    backer: 'Sequoia Economic Infrastructure Income Fund',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Residential specialist care',
  },

  {
    name: 'Argentum',
    aliases: ['Argentum', 'DE: Argentum', 'Argentum Gruppe'],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: 'Residential elderly care',
  },

  {
    name: 'Aria Care',
    aliases: ['Aria Care', 'GB: Aria Care'],
    backer: 'Welltower Inc.',
    backerType: 'reit',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },

  {
    name: 'Danforth Care Partners',
    aliases: ['Danforth Care Partners', 'GB: Danforth Care Partners'],
    backer: 'Welltower Inc.',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Residential elderly care',
  },

  {
    name: 'Esperi Care',
    aliases: ['Esperi Care', 'FI: Esperi Care'],
    backer: 'Triton',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Residential elderly care',
  },

  {
    name: 'Habitus',
    aliases: ['Habitus', 'DK: Habitus'],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: 'Residential specialist care',
  },

  {
    name: 'Hamberley Care Homes',
    aliases: ['Hamberley Care Homes', 'GB: Hamberley Care Homes'],
    backer: 'Patron Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Residential elderly care',
  },

  {
    name: 'Penta Hospitals International',
    aliases: [
      'Penta Hospitals',
      'CZ: Penta Hospitals',
      'Penta Hospitals International',
      'Südamekodud AS',
      'EE: Südamekodud AS',
      'GRUPA NOWY SZPITAL SPÓŁKA Z OGRANICZONĄ ODPOWIEDZIALNOŚCIĄ',
      'PL: GRUPA NOWY SZPITAL SPÓŁKA Z OGRANICZONĄ ODPOWIEDZIALNOŚCIĄ',
    ],
    backer: 'Penta Investments',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Residential elderly care',
  },

  {
    name: 'Silver Stream Healthcare',
    aliases: ['Silver Stream Healthcare', 'IE: Silver Stream Healthcare'],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: 'Residential elderly care',
  },

  {
    name: 'Sonas Group',
    aliases: [
      'Sonas Group / Medwind Holdings',
      'IE: Sonas Group / Medwind Holdings',
      'Sonas Group',
      'Medwind Holdings',
    ],
    backer: 'H.I.G. Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Residential elderly care',
  },

  {
    name: 'Tertianum',
    aliases: ['Tertianum', 'CH: Tertianum'],
    backer: 'Capvis',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Residential elderly care',
  },

  // ══ SPONSOR-RESOLUTION COMMISSION RETURN, 2026-08-20 ════════════════════════
  // 36 records from a research pass over the 300 largest unresolved economic
  // groups (COMMISSION_SPONSOR_CROSSWALK_2026-08-19). 44 worklist labels
  // reconciled; 251 rows were left explicitly unresolved rather than
  // force-matched, which was the instruction and is the right outcome.
  //
  // ⚠️ EVERY WORKLIST LABEL IS CARRIED AS AN ALIAS, INCLUDING THE 'XX: ' PREFIXED
  // FORM. `normaliseHoldco` strips only limited|ltd|plc|llp|uk|group|holdings, so
  // 'NL: Parnassia Groep' and 'Parnassia' do NOT match each other. Dropping the
  // prefixed form would leave the record unreachable from the data.
  //
  // ⚠️ AND THE FIVE "EXCLUDED" ROWS ARE RECORDED AS `foundation`, NOT OMITTED. The
  // return excluded Parnassia, Careyn, Espria, Fundis and Stichting Thebe from
  // COMMERCIAL sponsor resolution because they are foundation-governed Dutch
  // providers. That is itself a control verdict: a foundation-governed provider is
  // not a founder who can sell. Omitting them would leave them 'unknown', which
  // says less than the evidence supports.
  {
    name: 'Acibadem International Medical Center',
    aliases: ['Acibadem International Medical Center', 'NL: Acibadem International Medical Center'],
    backer: 'IHH Healthcare Berhad',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Dental',
  },

  {
    name: 'B. Braun',
    aliases: [
      'B. Braun / Elyse Klinieken',
      'B. Braun / ExpertCare',
      'NL: B. Braun / Elyse Klinieken',
      'NL: B. Braun / ExpertCare',
    ],
    backer: 'B. Braun',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Domiciliary / home care',
  },

  {
    name: 'BENU',
    aliases: ['BENU / PHOENIX group', 'LT: BENU / PHOENIX group', 'NL: BENU / PHOENIX group'],
    backer: 'Merckle family',
    backerType: 'family',
    confidence: 'high',
    sector: 'Pharmacy',
  },

  {
    name: 'Camelia (Nemuno vaistinė)',
    aliases: ['Camelia (Nemuno vaistinė)', 'LT: Camelia (Nemuno vaistinė)'],
    backer: 'Petras Bartkus',
    backerType: 'family',
    confidence: 'medium',
    sector: 'Pharmacy',
  },

  {
    name: 'Careyn',
    aliases: ['Careyn', 'NL: Stichting Careyn'],
    backer: null,
    backerType: 'foundation',
    confidence: 'high',
    sector: 'Residential elderly care',
  },

  {
    name: 'Country Court Care Group',
    aliases: ['Country Court Care Group', 'GB: Country Court Care Group', 'Kibo Holdings'],
    backer: 'Kachra family',
    backerType: 'family',
    confidence: 'high',
    sector: 'Residential elderly care',
  },

  {
    name: 'Dental Clinics',
    aliases: ['Dental Clinics', 'NL: Dental Clinics'],
    backer: 'Nordic Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Dental',
  },

  {
    name: 'DOMIDOM',
    aliases: ['DOMIDOM', 'DOMIDOM SERVICES'],
    backer: 'emeis',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Domiciliary / home care',
  },

  {
    name: 'Envisage Dental UK',
    aliases: ['Envisage Dental UK', 'Envisage Dental Uk'],
    backer: 'Harry Gill and Dr Sandy Dau',
    backerType: 'family',
    confidence: 'high',
    sector: 'Dental',
  },

  {
    name: 'Espria',
    aliases: ['Espria', 'NL: Espria'],
    backer: null,
    backerType: 'foundation',
    confidence: 'high',
    sector: 'Domiciliary / home care',
  },

  {
    name: 'EssilorLuxottica',
    aliases: ['EssilorLuxottica', 'FR: EssilorLuxottica'],
    backer: 'EssilorLuxottica',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Optical',
  },

  {
    name: 'Euroapotheca',
    aliases: ['Euroapotheca', 'LT: Euroapotheca / Vilniaus Prekyba'],
    backer: 'Vilniaus Prekyba',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Pharma wholesale',
  },

  {
    name: 'Eurofins Clinical Diagnostics',
    aliases: [
      'Eurofins Clinical Diagnostics',
      'FR: Eurofins Clinical Diagnostics / Eurofins Scientific',
    ],
    backer: 'Eurofins Scientific',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },

  {
    name: 'Falck Sverige',
    aliases: ['Falck Sverige', 'SE: Falck Sverige AB'],
    backer: 'Lundbeck Foundation',
    backerType: 'foundation',
    confidence: 'high',
    sector: 'Primary care (GP)',
  },

  {
    name: 'Forenede Care',
    aliases: ['Forenede Care', 'SE: Forenede Care'],
    backer: 'Forenede / Krogh family',
    backerType: 'family',
    confidence: 'high',
    sector: 'Domiciliary / home care',
  },

  {
    name: 'Frösunda',
    aliases: ['Frösunda', 'SE: Frösunda / Adolfsen ownership'],
    backer: 'Kristian and Roger Adolfsen',
    backerType: 'family',
    confidence: 'high',
    sector: 'Domiciliary / home care',
  },

  {
    name: 'Fundis',
    aliases: ['Fundis', 'NL: Fundis'],
    backer: null,
    backerType: 'foundation',
    confidence: 'high',
    sector: 'Domiciliary / home care',
  },

  {
    name: 'Groupe VYV',
    aliases: ['FR: Groupe VYV', 'Groupe VYV'],
    backer: null,
    backerType: 'cooperative',
    confidence: 'high',
    sector: 'Ambulance / patient transport',
  },

  {
    name: 'Harbour Healthcare',
    aliases: [
      'Cleveland Healthcare Group',
      'GB: Harbour Healthcare Group',
      'Harbour Healthcare',
      'Harbour Healthcare Holdings',
      'ROSEBERRY CARE CENTRES (ENGLAND) LTD',
      'Roseberry Care Centres',
    ],
    backer: 'Andrew Worsley family',
    backerType: 'family',
    confidence: 'high',
    sector: 'Residential elderly care',
  },

  {
    name: 'InMedica',
    aliases: ['InMedica / Meliva Lithuania'],
    backer: 'Mehiläinen, backed by CVC Capital Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: null,
  },

  {
    name: 'Iroise Bellevie',
    aliases: [
      'FR: Iroise Bellevie / Les Jardins d’Iroise',
      'Iroise Bellevie / Les Jardins d’Iroise',
    ],
    backer: null,
    backerType: 'family',
    confidence: 'high',
    sector: 'Residential elderly care',
  },

  {
    name: 'Keolis Santé',
    aliases: ['FR: Keolis Santé', 'Keolis Santé'],
    backer: 'Keolis',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Ambulance / patient transport',
  },

  {
    name: 'Kingsley Healthcare',
    aliases: ['GB: Kingsley Healthcare', 'Kingsley (KMT)', 'Kingsley (Kmt)', 'Kingsley Healthcare'],
    backer: 'Daya and Sumi Thayan',
    backerType: 'family',
    confidence: 'high',
    sector: 'Residential elderly care',
  },

  {
    name: 'OMERIS',
    aliases: ['FR: OMERIS', 'OMERIS'],
    backer: 'Soumali family',
    backerType: 'family',
    confidence: 'high',
    sector: 'Residential elderly care',
  },

  {
    name: 'Parnassia',
    aliases: ['NL: Parnassia Groep', 'Parnassia'],
    backer: null,
    backerType: 'foundation',
    confidence: 'high',
    sector: 'Hospitals & clinics',
  },

  {
    name: 'Prima Vård',
    aliases: ['Prima Vård / former Lideta'],
    backer: 'Nordstjernan',
    backerType: 'family',
    confidence: 'high',
    sector: null,
  },

  {
    name: 'QIAGEN',
    aliases: ['QIAGEN', 'Qiagen Nv'],
    backer: 'QIAGEN',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Physiotherapy & allied health',
  },

  {
    name: 'Riverdale Midco',
    aliases: ['Riverdale Midco'],
    backer: 'Apposite Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },

  {
    name: 'Réseau Vidi',
    aliases: ['FR: Réseau Vidi', 'Réseau Vidi'],
    backer: null,
    backerType: 'cooperative',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },

  {
    name: 'Sanofi',
    aliases: ['LT: Sanofi', 'Sanofi'],
    backer: 'Sanofi',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Pharma wholesale',
  },

  {
    name: 'Smart Dental Care',
    aliases: ['Smart Dental Care'],
    backer: 'Suken Shah',
    backerType: 'family',
    confidence: 'high',
    sector: 'Dental',
  },

  {
    name: 'Specsavers',
    aliases: ['Specsavers'],
    backer: 'Perkins family',
    backerType: 'family',
    confidence: 'high',
    sector: 'Medical equipment & supplies',
  },

  {
    name: 'Steva',
    aliases: ['FR: Groupe Steva / Villa Beausoleil', 'Steva / Villa Beausoleil'],
    backer: 'Laurent Boughaba',
    backerType: 'family',
    confidence: 'high',
    sector: 'Domiciliary / home care',
  },

  {
    name: 'Stichting Thebe Wonen en Zorg',
    aliases: ['NL: Stichting Thebe Wonen en Zorg', 'Stichting Thebe Wonen en Zorg'],
    backer: null,
    backerType: 'foundation',
    confidence: 'high',
    sector: 'Residential elderly care',
  },

  {
    name: 'Synergy Dental Clinic',
    aliases: ['GB: Synergy Dental Clinic', 'Synergy Dental Clinic'],
    backer: 'Dr Zuber Bagasi',
    backerType: 'family',
    confidence: 'medium',
    sector: 'Dental',
  },

  {
    name: 'ULOBA',
    aliases: ['NO: ULOBA - INDEPENDENT LIVING NORGE SA Group', 'ULOBA'],
    backer: null,
    backerType: 'cooperative',
    confidence: 'high',
    sector: 'Domiciliary / home care',
  },
  {
    name: 'Academia',
    aliases: ['Academia', 'ACADEMIA Holding GmbH', 'St. Vinzenz Klinik Pfronten im Allgäu GmbH'],
    backer: 'GREENPEAK Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Accès Vision',
    aliases: ['Accès Vision'],
    backer: 'M80 Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Achieve together',
    aliases: ['Achieve together', 'Care Management Group', 'Regard'],
    backer: "Ontario Teachers'",
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Residential specialist care',
  },
  {
    name: 'Active Care Group',
    aliases: ['Active Care Group', 'The Huntercombe Group', 'Christchurch'],
    backer: 'Sequoia Economic Infrastructure (Gadwall)',
    backerType: 'infra',
    confidence: 'medium',
    sector: 'Residential specialist care',
  },
  {
    name: 'Acura Zahnärzte',
    aliases: ['Acura Zahnärzte', 'Acura MVZ GmbH', 'Acura Dental MVZ GmbH', 'Acura Zahnärzte GmbH'],
    backer: 'Investcorp',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Dental',
  },
  {
    name: 'Affidea',
    aliases: ['Affidea', 'Euromedic', 'Q-Diagnostica', 'Orthoderm'],
    backer: 'Groupe Bruxelles Lambert',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Affidea España',
    aliases: ['Affidea España', 'Q Diagnóstica', 'Clínica Tecma', 'Clínica Atenea'],
    backer: 'Groupe Bruxelles Lambert',
    backerType: 'strategic',
    confidence: 'medium',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Alain Afflelou',
    aliases: ['Alain Afflelou', 'Afflelou Acousticien'],
    backer: 'Lion Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Audiology',
  },
  {
    name: 'Aleris',
    aliases: ['Aleris', 'Art Clinic'],
    backer: 'Triton Partners',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Physiotherapy & allied health',
  },
  {
    name: 'AllDent',
    aliases: ['AllDent', 'AllDent Zahnzentrum', 'AllDent Holding GmbH'],
    backer: 'Castik Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'Alliance Healthcare',
    aliases: [
      'Alliance Healthcare',
      'Alliance Healthcare (Distribution) Limited',
      'Alliance Healthcare Management Services',
      'Cencora',
      'AmerisourceBergen',
    ],
    backer: 'Cencora',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Pharma wholesale',
  },
  {
    name: 'Alliance Medical',
    aliases: [
      'Alliance Medical',
      'Alliance Medical Group',
      'AMG',
      'AMDI',
      'Alliance Medical Ireland',
    ],
    backer: 'iCON Infrastructure',
    backerType: 'infra',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Alloheim',
    aliases: [
      'Alloheim',
      'Alloheim Senioren-Residenzen',
      'Linimed',
      'Alloheim Gruppe',
      'DE: Alloheim Gruppe',
    ],
    backer: 'Nordic Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Domiciliary / home care',
  },
  {
    name: 'Almaviva Santé',
    aliases: ['Almaviva Santé'],
    backer: 'Wren House Infrastructure / Bpifrance',
    backerType: 'infra',
    confidence: 'medium',
    sector: '(out-of-panel) Hospitals',
  },
  {
    name: 'Altano Group',
    aliases: ['Altano Group', 'Altano', 'Altano-Gruppe', 'Altano Gruppe GmbH'],
    backer: 'Ufenau Capital Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'Ambea',
    aliases: [
      'Ambea',
      'Vardaga',
      'Nytida',
      'Stendi',
      'Altiden',
      'Klara',
      'AMB NORWAY',
      'NO: AMB NORWAY',
    ],
    backer: 'Ambea AB (listed)',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Residential elderly care',
  },
  {
    name: 'Amedes',
    aliases: ['Amedes', 'amedes', 'amedes Medizinische Dienstleistungen', 'MVZ Laaff'],
    backer: 'OMERS / Goldman Sachs AM / AXA IM',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'amedes fertility',
    aliases: ['amedes fertility', 'amedes Fertility Center'],
    backer: 'OMERS / Goldman Sachs AM / AXA IM',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Amplifon',
    aliases: ['Amplifon', 'GAES', 'Bay Audio', 'Miracle-Ear'],
    backer: 'Amplifon SpA (listed)',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Audiology',
  },
  {
    name: 'Anima Care',
    aliases: ['Anima Care', 'Anima', 'Anima Cura'],
    backer: 'AG Insurance',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Residential elderly care',
  },
  {
    name: 'Argos Vétérinaire',
    aliases: ['Argos Vétérinaire', 'Argos'],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: 'Veterinary',
  },
  {
    name: 'Armonea',
    aliases: ['Armonea', 'Restel Résidences', 'Group Van den Brande'],
    backer: 'EQT Infrastructure',
    backerType: 'infra',
    confidence: 'high',
    sector: 'Residential elderly care',
  },
  {
    name: 'Artemis Augenkliniken',
    aliases: ['Artemis Augenkliniken', 'Artemis Augenkliniken und MVZ'],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: 'Specialist medical',
  },
  {
    name: 'Arts en Zorg',
    aliases: ['Arts en Zorg'],
    backer: 'NPM Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: '(out-of-panel) Primary care',
  },
  {
    name: 'Ascenti Group',
    aliases: ['Ascenti Group', 'Ascenti Physio', 'Six Physio', 'Advo Health'],
    backer: 'bd-capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Physiotherapy & allied health',
  },
  {
    name: 'Ascires',
    aliases: ['Ascires', 'Cetir', 'ERESA', 'Sistemas Genómicos'],
    backer: null,
    backerType: 'family',
    confidence: 'medium',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'ASISA Dental',
    aliases: ['ASISA Dental', 'ASISA Dental SAU', 'Lavinia'],
    backer: null,
    backerType: 'cooperative',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'Aspris',
    aliases: ['Aspris', "Aspris Children's Services", 'Sandcastle Care'],
    backer: 'Waterland Private Equity',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Residential specialist care',
  },
  {
    name: 'Attendo',
    aliases: ['Attendo'],
    backer: 'Attendo AB (listed)',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Domiciliary / home care',
  },
  {
    name: 'Audilab',
    aliases: ['Audilab'],
    backer: 'Demant',
    backerType: 'strategic',
    confidence: 'medium',
    sector: 'Audiology',
  },
  {
    name: 'AudioNova Italia',
    aliases: ['AudioNova Italia'],
    backer: 'Sonova',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Audiology',
  },
  {
    name: 'Audiovita',
    aliases: ['Audiovita'],
    backer: 'Demant',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Audiology',
  },
  {
    name: 'Avelia',
    aliases: ['Avelia', 'Alleanza Veterinaria Italia'],
    backer: 'Cathay Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'Avery Healthcare',
    aliases: ['Avery Healthcare', 'Avery'],
    backer: 'Welltower',
    backerType: 'reit',
    confidence: 'high',
    sector: 'Residential elderly care',
  },
  {
    name: 'B. Braun via medis / DTZ',
    aliases: [
      'B. Braun via medis / DTZ',
      'B. Braun via medis GmbH',
      'B. Braun Gesundheitszentren Dienstleistungs GmbH',
      'DTZ Dialyse-Trainings-Zentren GmbH',
    ],
    backer: 'B. Braun SE',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Ballesol',
    aliases: ['Ballesol', 'Intercentros Ballesol'],
    backer: 'Santalucía Seguros',
    backerType: 'strategic',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'Barchester Healthcare',
    aliases: ['Barchester Healthcare', 'Barchester'],
    backer: 'Welltower',
    backerType: 'reit',
    confidence: 'high',
    sector: 'Residential elderly care',
  },
  {
    name: 'Bergman Clinics',
    aliases: ['Bergman Clinics', 'Memira', 'Dermatologikum'],
    backer: 'Triton Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Bianalisi',
    aliases: ['Bianalisi'],
    backer: 'Charme Capital Partners',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Biogroup',
    aliases: ['Biogroup', 'Biogroup-LCD', 'LBI'],
    backer: null,
    backerType: 'family',
    confidence: 'medium',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Bloezem Woonzorg',
    aliases: ['Bloezem Woonzorg', 'Wonen bij September', 'Van Hollant', 'Bloemendael'],
    backer: 'emeis',
    backerType: 'strategic',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'Bodyset',
    aliases: ['Bodyset'],
    backer: 'Osmond Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Physiotherapy & allied health',
  },
  {
    name: 'Bupa',
    aliases: [
      'Bupa',
      'Bupa Dental Care',
      'Oasis Healthcare',
      'Oasis Dental Care',
      'Smiles Dental',
      'Sanitas',
      'Sanitas Dental',
      'Sanitas Mayores',
      'Sanitas Residencial',
      'Bupa Care Homes',
      'Richmond Villages',
    ],
    backer: null,
    backerType: 'strategic',
    confidence: 'high',
    sector: '(out-of-panel) Hospitals',
  },
  {
    name: "Ca'Zampa Group",
    aliases: [
      "Ca'Zampa Group",
      "Ca'Zampa",
      'BluVet',
      'Anubi',
      'Campanale',
      'Vigna Clara',
      'Pinerolesi',
    ],
    backer: 'Nextalia SGR',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'CAB Polidiagnostico',
    aliases: ['CAB Polidiagnostico', 'CAB Polidiagnostico S.p.A.'],
    backer: 'Lifenet Healthcare',
    backerType: 'strategic',
    confidence: 'medium',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Care Fertility Group',
    aliases: ['Care Fertility Group', 'Care Fertility', 'CARE'],
    backer: 'Nordic Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Care UK',
    aliases: ['Care UK'],
    backer: 'Welltower',
    backerType: 'reit',
    confidence: 'high',
    sector: 'Residential elderly care',
  },
  {
    name: 'CareChoice',
    aliases: ['CareChoice'],
    backer: 'InfraVia Capital Partners',
    backerType: 'infra',
    confidence: 'high',
    sector: 'Residential elderly care',
  },
  {
    name: 'CareDent Italia',
    aliases: ['CareDent Italia', 'Caredent Italia S.p.A.', 'Smileclin'],
    backer: 'L Catterton',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Dental',
  },
  {
    name: 'CareHub',
    aliases: ['CareHub', 'Brexia Med S.r.l.', 'Cormedica', 'Poliambulatori San Gaetano'],
    backer: 'Quadrivio Group',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'CareTech',
    aliases: [
      'CareTech',
      'Caretech Community',
      'Cambian',
      'Spark of Genius',
      'Smartbox',
      'By the Bridge',
      'Amalfi Midco',
    ],
    backer: 'DBAY Advisors',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Residential specialist care',
  },
  {
    name: 'Caservet',
    aliases: ['Caservet'],
    backer: 'Caser / Helvetia',
    backerType: 'strategic',
    confidence: 'medium',
    sector: 'Veterinary',
  },
  {
    name: 'Centric Health',
    aliases: ['Centric Health', 'Swiftcare', 'Locumotion', 'Global Diagnostics'],
    backer: 'Five Arrows / Rothschild & Co',
    backerType: 'pe',
    confidence: 'high',
    sector: '(out-of-panel) Primary care',
  },
  {
    name: 'Centro Medico Santagostino',
    aliases: ['Centro Medico Santagostino', 'Società e Salute S.p.A.', 'Dyadea'],
    backer: 'UnipolSai',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Cera',
    aliases: ['Cera', 'Cera Care', 'First City Nursing', 'Care at Home Services'],
    backer: 'BDT & MSD Partners / Schroders Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Domiciliary / home care',
  },
  {
    name: 'Cerba HealthCare',
    aliases: [
      'Cerba HealthCare',
      'Cerba',
      'Cerballiance',
      'Lifebrain',
      'Cerba Lifebrain Italia',
      'Cerba Research',
      'Barc',
    ],
    backer: 'Partners Group / PSP Investments',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Cerba Internacional SAE',
    aliases: ['Cerba Internacional SAE', 'Analiza', 'Laboratorios Analiza'],
    backer: 'Biogroup',
    backerType: 'strategic',
    confidence: 'medium',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Charleston',
    aliases: ['Charleston', 'Charleston Holding'],
    backer: 'KOS Group / Primonial REIM',
    backerType: 'strategic',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'City & County Healthcare Group',
    aliases: ['City & County Healthcare Group', 'City & County', 'Comfort Call', 'Complete Care'],
    backer: 'Graphite Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Domiciliary / home care',
  },
  {
    name: 'Clariane',
    aliases: [
      'Clariane',
      'Korian',
      'Ages & Vie',
      'Curanum',
      'Stella Vitalis',
      'Senior Cottages',
      'Segesta',
      'Senior Living Group',
      'Stepping Stones',
      'Clariane Spain',
      'ES: Clariane Spain',
      'SEGESTA S.P.A. SB',
    ],
    backer: 'Crédit Agricole Assurances (largest holder)',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Domiciliary / home care',
  },
  {
    name: 'Clinadent',
    aliases: ['Clinadent'],
    backer: 'Qualium Investissement',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'Clinias Dental Group',
    aliases: ['Clinias Dental Group', 'Clinias Dental Group B.V.'],
    backer: 'Bencis Capital Partners',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'Clínica Baviera',
    aliases: ['Clínica Baviera', 'Clinica Baviera Italia', 'Niavision'],
    backer: 'Aier Eye Hospital Group',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Clinique des Champs-Elysées',
    aliases: ['Clinique des Champs-Elysées', 'Groupe Clinique des Champs-Elysées', 'CRPCE'],
    backer: 'Raise Investissement',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Specialist medical',
  },
  {
    name: 'Colisée',
    aliases: ['Colisée', 'Armonea', 'Mederi', 'Amavida'],
    backer: 'EQT Infrastructure',
    backerType: 'infra',
    confidence: 'low',
    sector: 'Domiciliary / home care',
  },
  {
    name: 'Colosseum Dental Group',
    aliases: [
      'Colosseum Dental Group',
      'Colosseum Dental',
      'Colosseum Dental GmbH',
      'Colosseum Smile',
      'Smile Tandvård',
      'Smile.se',
      'Adent',
      'Dentelia',
      'Odontosalute',
      'Apollonia',
      'Progetto Dentale',
      'Curaeos',
    ],
    backer: 'Jacobs Holding',
    backerType: 'family',
    confidence: 'high',
    sector: 'Dental',
  },
  {
    name: 'Community Health and Eyecare',
    aliases: ['Community Health and Eyecare', 'CHEC', 'Community Health and Eyecare Ltd'],
    backer: 'G Square Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Compass Community',
    aliases: ['Compass Community', 'Compass Fostering', "Compass Children's Homes", 'Liberi Topco'],
    backer: 'Cap10 Partners',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Residential specialist care',
  },
  {
    name: 'Confident',
    aliases: ['Confident', 'Confident S.p.A. SB'],
    backer: 'Kyip Capital SGR',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'Cora Health',
    aliases: ['Cora Health', 'Connect Health', 'Healthshare'],
    backer: 'LDC',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Physiotherapy & allied health',
  },
  {
    name: 'CORIUS',
    aliases: [
      'CORIUS',
      'CORIUS Gruppe',
      'CORIUS Group',
      'MVZ Corius München GmbH',
      'MVZ Hautzentrum an der Oper',
      'Meyer-Rogge',
      'MediCorium',
    ],
    backer: 'Ufenau Capital Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Corus',
    aliases: ['Corus', 'Corus Dental Institute SL', 'Signadens'],
    backer: 'Careventures',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Dental',
  },
  {
    name: 'Cosmetic Physician Partners',
    aliases: [
      'Cosmetic Physician Partners',
      'CPP',
      'CPP Europe',
      'CPPE',
      'Medispa Group',
      'The Clinic Holland Park',
      'Define Clinic',
      'Beacon Face & Dermatology',
    ],
    backer: null,
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Specialist medical',
  },
  {
    name: 'Creature Comforts',
    aliases: ['Creature Comforts', 'Creature Comforts Group', 'Creature Comforts Group Limited'],
    backer: 'Sofina',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'CVS Group',
    aliases: ['CVS Group', 'CVS', 'CVS Group plc', 'Vet Connect', 'Healthy Pet Club'],
    backer: null,
    backerType: 'listed',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'Cygnet Health Care',
    aliases: ['Cygnet Health Care', 'Cygnet', 'Cambian', 'Cygnet Group', 'Cygnet Social Care'],
    backer: 'Universal Health Services',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Residential specialist care',
  },
  {
    name: 'DaVita Germany',
    aliases: ['DaVita Germany', 'DaVita Deutschland AG'],
    backer: 'DaVita Inc.',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'De Tandartsengroep',
    aliases: ['De Tandartsengroep', 'De Tandartsengroep B.V.', 'DTG'],
    backer: 'Swedish Oral Care',
    backerType: 'strategic',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'Deine Urologie',
    aliases: ['Deine Urologie', 'Deine Urologie HoldCo GmbH'],
    backer: 'Summit Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Demant (retail)',
    aliases: [
      'Demant (retail)',
      'Audika',
      'Hidden Hearing',
      'KIND',
      'HearingLife',
      'Western Hearing',
    ],
    backer: 'William Demant Foundation',
    backerType: 'foundation',
    confidence: 'high',
    sector: 'Audiology',
  },
  {
    name: 'Dental Care Ireland',
    aliases: ['Dental Care Ireland', 'Dental Care Ireland Group'],
    backer: 'Zeme Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'Dental Company',
    aliases: ['Dental Company'],
    backer: 'Smart Holding',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Dental',
  },
  {
    name: 'Dental21',
    aliases: ['Dental21', 'Dental21 GmbH', 'Patient21 SE'],
    backer: 'Target Global / Piton / Pitango',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'DentalPro',
    aliases: [
      'DentalPro',
      'Gruppo Giovanni Bona',
      'Giovanni Bona Cliniche Dentali',
      'Dentadent',
      'Vitaldent Italia',
    ],
    backer: 'BC Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Dental',
  },
  {
    name: 'Denteam',
    aliases: ['Denteam'],
    backer: 'G Square Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'Dentego',
    aliases: ['Dentego', 'Adental Groupe', 'Adental'],
    backer: 'G Square Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'Dentix',
    aliases: ['Dentix'],
    backer: 'KKR',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'Dentylis',
    aliases: ['Dentylis', 'Centres de Santé Dentaire et Médical'],
    backer: 'MBO+',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Dental',
  },
  {
    name: 'DermaHaven',
    aliases: ['DermaHaven', 'DermaHaven B.V.'],
    backer: 'Erasmus MC',
    backerType: 'strategic',
    confidence: 'medium',
    sector: 'Specialist medical',
  },
  {
    name: 'DERMANOVIS',
    aliases: ['DERMANOVIS', 'DERMANOVIS GmbH', 'St. Johannes Krankenhaus GmbH (Kamenz)'],
    backer: 'Summit Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Diaverum Germany',
    aliases: ['Diaverum Germany', 'Diaverum Deutschland GmbH'],
    backer: 'Bridgepoint',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Dierenartsen Groep Nederland',
    aliases: ['Dierenartsen Groep Nederland', 'DGN B.V.'],
    backer: 'BC Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'Docali / Effi-centres',
    aliases: ['Docali / Effi-centres', 'Docali', 'Efficentres Développement'],
    backer: 'Caravelle',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'Doktor.de',
    aliases: ['Doktor.de', 'Doktorde GmbH', 'Doktor.se'],
    backer: 'Doktor.se',
    backerType: 'strategic',
    confidence: 'medium',
    sector: '(out-of-panel) Primary care',
  },
  {
    name: 'Domidep',
    aliases: ['Domidep', 'Vitanas', 'Vitanas Gruppe', 'DE: Vitanas Gruppe'],
    backer: 'I Squared Capital',
    backerType: 'infra',
    confidence: 'high',
    sector: 'Residential elderly care',
  },
  {
    name: 'Domus Magnus',
    aliases: ['Domus Magnus'],
    backer: 'NPM Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'DomusVi',
    aliases: ['DomusVi', 'Geriatros', 'SARquavitae', 'DomusVi Domicile', 'Stella'],
    backer: 'ICG',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Domiciliary / home care',
  },
  {
    name: 'Donte Group',
    aliases: [
      'Donte Group',
      'Donte Group SL',
      'Vitaldent',
      'Smysecret',
      'Moonz',
      'MAEX',
      'B87619698',
    ],
    backer: 'Advent International',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Dental',
  },
  {
    name: 'DPH Dental Partner Holding',
    aliases: ['DPH Dental Partner Holding', 'DPH Dental Partner Holding GmbH', 'Zahnstation'],
    backer: 'Nordic Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'Dutch Dental Group',
    aliases: ['Dutch Dental Group', 'Dutch Dental Group B.V.'],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: 'Dental',
  },
  {
    name: 'Elsan',
    aliases: ['Elsan'],
    backer: 'CVC / Tethys Invest',
    backerType: 'pe',
    confidence: 'high',
    sector: '(out-of-panel) Hospitals',
  },
  {
    name: 'Elysium Healthcare',
    aliases: ['Elysium Healthcare', 'Elysium', 'Partnerships in Care'],
    backer: 'Ramsay Health Care',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'emeis',
    aliases: [
      'emeis',
      'Orpea',
      'Clinea',
      'FirstCare',
      'TLC',
      'Senevita',
      'Sanyres',
      'Brindley',
      'Adhap',
      'Domidom',
      'MediCare (emeis)',
      'DE: MediCare (emeis)',
    ],
    backer: 'Caisse des Dépôts consortium',
    backerType: 'sovereign',
    confidence: 'high',
    sector: 'Domiciliary / home care',
  },
  {
    name: 'Emera Group',
    aliases: ['Emera Group', 'Emera', 'Virtue'],
    backer: null,
    backerType: 'family',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'EMVIA Living',
    aliases: ['EMVIA Living', 'EMVIA', 'MED+ORG'],
    backer: 'Chequers Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'Equipe Zorgbedrijven',
    aliases: [
      'Equipe Zorgbedrijven',
      'Equipe Zorgbedrijven B.V.',
      'Xpert Clinics',
      'Xpert Handtherapie',
      'Velthuis Kliniek',
      'CosMedic',
      'Oogkliniek Heuvelrug',
    ],
    backer: 'Nordic Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'EssilorLuxottica (retail)',
    aliases: [
      'EssilorLuxottica (retail)',
      'GrandVision',
      'Vision Express',
      'Pearle',
      'Apollo-Optik',
      'Salmoiraghi & Viganò',
      'Eye Wish',
      'GrandOptical',
      'Synoptik',
      'Brilleland',
      'Interoptik',
    ],
    backer: 'EssilorLuxottica (listed)',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Optical',
  },
  {
    name: 'Eugin Group',
    aliases: ['Eugin Group', 'Eugin', 'Ginefiv', 'CREA', 'FertiCare'],
    backer: 'GED Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Eurofins (clinical Dx)',
    aliases: [
      'Eurofins (clinical Dx)',
      'Eurofins Biomnis',
      'Eurofins Megalab',
      'Eurofins NMDL-LCPL',
    ],
    backer: 'Eurofins Scientific',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'European Dental Group',
    aliases: [
      'European Dental Group',
      'EDG',
      'Dental Clinics Nederland',
      'Top Mondzorg B.V.',
      'TopOrtho',
      'Adent',
      'Zahnstation',
      'Dental Beauty Partners',
      'uwmond.be',
      'Fresh Tandartsen',
      'EDG Deutschland Holding GmbH',
    ],
    backer: 'Nordic Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Dental',
  },
  {
    name: 'Euryale Healthcare Italia 1',
    aliases: ['Euryale Healthcare Italia 1', 'Kryalos SGR'],
    backer: 'Kryalos SGR',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'Everlight Radiology',
    aliases: ['Everlight Radiology'],
    backer: 'Livingbridge',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Evidia',
    aliases: ['Evidia', 'Meine Radiologie', 'MRH', 'blikk', 'Aleris Imaging', '4Ways Diagnostics'],
    backer: 'EQT Infrastructure',
    backerType: 'infra',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Excellence Imagerie',
    aliases: ['Excellence Imagerie', 'Excellence Imagerie SAS', 'Imagerie Développement SAS'],
    backer: 'Metric Capital Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'EyeCare Group',
    aliases: [
      'EyeCare Group',
      'Bril In Mode',
      'City Lens',
      'Iris Optiek',
      'Saton Optiek',
      'Van der Leeuw Optiek',
    ],
    backer: 'EMK Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Audiology',
  },
  {
    name: 'Eyescan',
    aliases: ['Eyescan', 'Eyescan Oogziekenhuizen', 'Oogkliniek De Horsten', 'Rijnzicht'],
    backer: 'Ober Scharrer Gruppe',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Fielmann Group',
    aliases: ['Fielmann Group', 'Fielmann', 'SVS Vision', 'Befree'],
    backer: 'Fielmann family (listed)',
    backerType: 'family',
    confidence: 'high',
    sector: 'Audiology',
  },
  {
    name: 'Four Seasons Health Care',
    aliases: ['Four Seasons Health Care', 'Four Seasons', 'brighterkind'],
    backer: 'H/2 Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'Fovéa',
    aliases: ['Fovéa'],
    backer: 'EMZ Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'France Imageries Territoires',
    aliases: ['France Imageries Territoires', 'FIT'],
    backer: 'IDIA Capital Investissement',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'FutureLife Group',
    aliases: [
      'FutureLife Group',
      'FutureLife',
      'GENNET',
      'CRGH',
      'Bristol CRM',
      'Herts & Essex Fertility',
      'Bahceci',
      'Serpha',
    ],
    backer: 'CVC Capital / Hartenberg',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Fysio Groep Nederland',
    aliases: ['Fysio Groep Nederland', 'Fysiogroep Nederland B.V.', 'FGN'],
    backer: 'HC Partners',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Physiotherapy & allied health',
  },
  {
    name: 'G-21',
    aliases: ['G-21', 'G-21 S.r.l.', 'Domus Aurea S.r.l.'],
    backer: 'G Square Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Physiotherapy & allied health',
  },
  {
    name: 'Garofalo Health Care',
    aliases: [
      'Garofalo Health Care',
      'Garofalo Health Care S.p.A.',
      'GHC',
      'Gruppo Aurelia',
      'Domus Nova',
    ],
    backer: 'Garofalo Health Care (listed)',
    backerType: 'listed',
    confidence: 'high',
    sector: '(out-of-panel) Hospitals',
  },
  {
    name: 'GeneraLife',
    aliases: ['GeneraLife', 'Ginemed'],
    backer: 'KKR',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Gesundheitscampus Loreley',
    aliases: ['Gesundheitscampus Loreley', 'Gesundheitscampus Loreley GmbH'],
    backer: 'Centric Health',
    backerType: 'pe',
    confidence: 'medium',
    sector: '(out-of-panel) Primary care',
  },
  {
    name: 'GIG',
    aliases: [
      'GIG',
      'GIG Gesellschaft für integrierte Gesundheitsversorgung mbH',
      'HIG Holding für integrierte Gesundheitsversorgung GmbH',
    ],
    backer: 'Quadriga Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Groupe Inovie',
    aliases: ['Groupe Inovie', 'Inovie', 'Labosud', 'Labosud Provence Biologie', 'Medipath'],
    backer: 'Ardian',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Grupo Albertia',
    aliases: ['Grupo Albertia', 'Albertia Servicios Sociosanitarios SA'],
    backer: 'Wellder SOCIMI',
    backerType: 'reit',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'Grupo Casaverde',
    aliases: ['Grupo Casaverde', 'Casaverde Neurorrehabilitación', 'Casaverde Mayores'],
    backer: null,
    backerType: 'family',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'Grupo Pedro Jaén',
    aliases: ['Grupo Pedro Jaén', 'Grupo de Dermatología Pedro Jaén SL'],
    backer: 'Moira Capital Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Grupo Recoletas',
    aliases: ['Grupo Recoletas', 'Grupo Hospitalario Recoletas SL', 'Recoletas Salud'],
    backer: null,
    backerType: 'family',
    confidence: 'high',
    sector: '(out-of-panel) Hospitals',
  },
  {
    name: 'Gruppo Animalia',
    aliases: ['Gruppo Animalia', 'Animalia'],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: 'Veterinary',
  },
  {
    name: 'Gruppo Demas',
    aliases: ['Gruppo Demas', 'Demas S.r.l.', 'Veterinaria Tridentina', 'Punto Azzurro'],
    backer: 'LBO France',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'Gruppo Pavanello',
    aliases: ['Gruppo Pavanello'],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Gruppo San Donato',
    aliases: ['Gruppo San Donato', 'GSD', 'San Donato', 'San Raffaele'],
    backer: 'Rotelli family',
    backerType: 'family',
    confidence: 'medium',
    sector: '(out-of-panel) Hospitals',
  },
  {
    name: 'Gruppo Tronchet',
    aliases: ['Gruppo Tronchet'],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Gruppo Zaffiro',
    aliases: ['Gruppo Zaffiro'],
    backer: 'Eurizon Capital SGR',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Residential elderly care',
  },
  {
    name: 'GVM (Gruppo Villa Maria)',
    aliases: ['GVM (Gruppo Villa Maria)', 'GVM Care & Research', 'GVM'],
    backer: null,
    backerType: 'family',
    confidence: 'medium',
    sector: '(out-of-panel) Hospitals',
  },
  {
    name: 'Hallo Healthcare Group',
    aliases: [
      'Hallo Healthcare Group',
      'Admenta UK',
      'Admenta UK Limited',
      'AAH Pharmaceuticals',
      'AAH Pharmaceuticals Limited',
    ],
    backer: 'AURELIUS Group',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Pharma wholesale',
  },
  {
    name: 'HC-One',
    aliases: ['HC-One', 'HC-One Oval'],
    backer: 'Welltower',
    backerType: 'reit',
    confidence: 'high',
    sector: 'Residential elderly care',
  },
  {
    name: 'HCRG Care Group',
    aliases: [
      'HCRG Care Group',
      'Virgin Care',
      'Operose Health',
      'AT Medics',
      'The Practice Group',
    ],
    backer: 'Twenty20 Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: '(out-of-panel) Primary care',
  },
  {
    name: 'Hedonai',
    aliases: ['Hedonai', 'Medical Beauty Care Holding', 'Ivalia'],
    backer: 'Sherpa Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Helping Hands',
    aliases: ['Helping Hands', 'Helping Hands Home Care'],
    backer: 'Livingbridge',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Domiciliary / home care',
  },
  {
    name: 'Hestia Alliance',
    aliases: ['Hestia Alliance'],
    backer: 'Waterland Private Equity',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Residential elderly care',
  },
  {
    name: 'HM Hospitales',
    aliases: ['HM Hospitales', 'Grupo Hospitalario HM'],
    backer: null,
    backerType: 'family',
    confidence: 'high',
    sector: '(out-of-panel) Hospitals',
  },
  {
    name: 'Horizon Care & Education',
    aliases: ['Horizon Care & Education', 'Horizon Care'],
    backer: 'Graphite Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Residential specialist care',
  },
  {
    name: 'Hospiten',
    aliases: ['Hospiten'],
    backer: null,
    backerType: 'family',
    confidence: 'high',
    sector: '(out-of-panel) Hospitals',
  },
  {
    name: 'HT Médica',
    aliases: ['HT Médica', 'Grupo HT', 'Clínicas HT', 'DADISA', 'RM', 'SCANNER'],
    backer: 'Fremman Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Humana',
    aliases: ['Humana', 'Humana Individ & Familj'],
    backer: 'Humana AB (listed)',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Domiciliary / home care',
  },
  {
    name: 'Ideal Carehomes',
    aliases: ['Ideal Carehomes', 'LNT Care'],
    backer: 'Warwick Capital Partners',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'Imadis',
    aliases: ['Imadis', 'Deeplink Medical', 'Concrete Holding'],
    backer: 'Concrete Holding',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Imaneo',
    aliases: ['Imaneo', 'GIE Imaneo', 'Topco Imaneo SAS'],
    backer: 'Parquest Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Imapôle / ImaOne',
    aliases: ['Imapôle / ImaOne', 'Imapôle', 'ImaOne'],
    backer: 'Eurazeo',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Impress Group',
    aliases: [
      'Impress Group',
      'Impress',
      'Smile2Impress',
      'uniformteeth',
      'DrSmile',
      'Sunshine Smile GmbH',
    ],
    backer: null,
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'InHealth Group',
    aliases: ['InHealth Group', 'InHealth', 'Vista Health', 'TAC Healthcare', 'Preventicum'],
    backer: 'The Damask Trust',
    backerType: 'family',
    confidence: 'medium',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Inicea',
    aliases: ['Inicea', 'Cliniques Inicéa'],
    backer: 'Clariane',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Insparya',
    aliases: ['Insparya', 'Insparya Science & Clinical Institute'],
    backer: 'Vallis Capital Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Institut Marquès',
    aliases: ['Institut Marquès', 'FIV Valencia'],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: 'Specialist medical',
  },
  {
    name: 'Instituto Bernabeu',
    aliases: ['Instituto Bernabeu'],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: 'Specialist medical',
  },
  {
    name: 'Ipso Santé',
    aliases: ['Ipso Santé', 'GIE Ipso'],
    backer: 'Citizen Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: '(out-of-panel) Primary care',
  },
  {
    name: 'IVC Evidensia',
    aliases: [
      'IVC Evidensia',
      'Independent Vetcare',
      'IVC',
      'Evidensia',
      'Medivet',
      'VetOne',
      'Sterkliniek',
      'Dyrenes Venn',
      'Independent Vetcare Ltd',
      'Evidensia Djursjukvård',
    ],
    backer: 'EQT / Silver Lake',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'IVF-Life Group',
    aliases: ['IVF-Life Group', 'IVF-Life', 'IVF Spain', 'Instituto Vasco de Fertilidad'],
    backer: 'Care Fertility',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Specialist medical',
  },
  {
    name: 'IVI-RMA Global',
    aliases: ['IVI-RMA Global', 'IVI', 'RMA', 'Boston IVF', 'TRIO', 'IVIRMA'],
    backer: 'KKR',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Juneco',
    aliases: ['Juneco'],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: 'Specialist medical',
  },
  {
    name: 'Keys Group',
    aliases: ['Keys Group', 'Keys', 'Accomplish', 'ALFA', 'ADHD360', 'Mable', 'Peak'],
    backer: 'G Square Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Residential specialist care',
  },
  {
    name: 'KfH',
    aliases: ['KfH', 'KfH Kuratorium für Dialyse und Nierentransplantation e.V.'],
    backer: null,
    backerType: 'foundation',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'KineQuantum',
    aliases: ['KineQuantum'],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: 'Physiotherapy & allied health',
  },
  {
    name: 'Kinios',
    aliases: ['Kinios', 'Kinios GmbH', 'TMVZ'],
    backer: 'Triton',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'KOS Group',
    aliases: ['KOS Group', 'KOS', 'Anni Azzurri', 'Santo Stefano', 'Medipass', 'Charleston'],
    backer: 'F2i / CIR',
    backerType: 'infra',
    confidence: 'high',
    sector: '(out-of-panel) Hospitals',
  },
  {
    name: 'Kraamzus',
    aliases: ['Kraamzus', 'Kraamzus B.V.'],
    backer: 'Capital A',
    backerType: 'pe',
    confidence: 'medium',
    sector: '(out-of-panel) Maternity',
  },
  {
    name: 'Krys Group',
    aliases: ['Krys Group', 'Krys', 'Vision Plus', 'Lynx Optique', 'Krys Audition'],
    backer: null,
    backerType: 'cooperative',
    confidence: 'medium',
    sector: 'Audiology',
  },
  {
    name: 'Laboratorio Echevarne',
    aliases: ['Laboratorio Echevarne', 'Echevarne'],
    backer: null,
    backerType: 'family',
    confidence: 'medium',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Lasermed (Optegra DE)',
    aliases: [
      'Lasermed (Optegra DE)',
      'Optegra MVZ GmbH',
      'Optegra Deutschland',
      'Lasermed',
      'Memira DE',
    ],
    backer: 'MidEuropa',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Specialist medical',
  },
  {
    name: 'Lazeo',
    aliases: ['Lazeo'],
    backer: 'Blackstone',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Leyhoeve',
    aliases: ['Leyhoeve', 'Leyhoeve Zorg B.V.', 'Leyhoeve Vastgoed B.V.'],
    backer: 'Heitman',
    backerType: 'reit',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'Lifeanalytics',
    aliases: ['Lifeanalytics', 'Lifeanalytics S.p.A.'],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'LifeLink Medical',
    aliases: ['LifeLink Medical', 'LifeLink Medical GmbH'],
    backer: 'Partners Group',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Lifenet Healthcare',
    aliases: [
      'Lifenet Healthcare',
      'Lifenet S.p.A.',
      'Centro Medico Visconti di Modrone',
      'Eyecare Clinic',
      'Istituto Fanfani',
      'Valdisieve Hospital',
    ],
    backer: 'Reale Group',
    backerType: 'strategic',
    confidence: 'high',
    sector: '(out-of-panel) Hospitals',
  },
  {
    name: 'Lifeways Group',
    aliases: ['Lifeways Group', 'Lifeways Community Care'],
    backer: 'OCM',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Residential specialist care',
  },
  {
    name: 'Lillian Care',
    aliases: ['Lillian Care', 'Lillian Care GmbH'],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: '(out-of-panel) Primary care',
  },
  {
    name: 'Limbach Gruppe',
    aliases: ['Limbach Gruppe', 'Limbach', 'MVZ Labor Dr. Limbach & Kollegen'],
    backer: null,
    backerType: 'family',
    confidence: 'medium',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Livi / Kry',
    aliases: ['Livi / Kry', 'Livi', 'Kry'],
    backer: 'Index Ventures / Accel',
    backerType: 'pe',
    confidence: 'medium',
    sector: '(out-of-panel) Primary care',
  },
  {
    name: 'LNA Santé',
    aliases: ['LNA Santé', 'Le Noble Age', 'Nobilise', 'Financière Vertavienne 44'],
    backer: 'LNA Santé (listed)',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Residential elderly care',
  },
  {
    name: 'Maisons de Famille / Amavir',
    aliases: ['Maisons de Famille / Amavir', 'Maisons de Famille', 'Amavir', 'Amma', 'Adavir'],
    backer: 'Creadev',
    backerType: 'family',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'Maria Mallaband Care Group',
    aliases: ['Maria Mallaband Care Group', 'Maria Mallaband', 'MMCG', 'Countrywide Care Homes'],
    backer: null,
    backerType: 'family',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'Mars Veterinary Health',
    aliases: [
      'Mars Veterinary Health',
      'AniCura',
      'Linnaeus',
      'Willows',
      'Eastcott',
      'Davies',
      'Vetsum',
    ],
    backer: 'Mars',
    backerType: 'family',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'Med 360°',
    aliases: ['Med 360°', 'Med 360° SE', 'MED360° MVZ', 'Conradia Radiologie', 'Conradia'],
    backer: 'Sana Kliniken AG',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'MED ON',
    aliases: ['MED ON', 'MED ON Holding GmbH', 'MED:ON'],
    backer: 'Triton',
    backerType: 'pe',
    confidence: 'high',
    sector: '(out-of-panel) Primary care',
  },
  {
    name: 'medermis clinics',
    aliases: ['medermis clinics', 'medermis clinics GmbH'],
    backer: 'EOS Capital Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'MEDIAN Group',
    aliases: ['MEDIAN Group', 'MEDIAN Kliniken'],
    backer: 'Waterland Private Equity',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Physiotherapy & allied health',
  },
  {
    name: 'Medica Group',
    aliases: ['Medica Group', 'Medica', 'Medica Reporting', 'Axon Diagnostics'],
    backer: 'IK Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Medicover Germany',
    aliases: ['Medicover Germany', 'Medicover GmbH'],
    backer: 'Medicover AB',
    backerType: 'listed',
    confidence: 'medium',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Medivet',
    aliases: ['Medivet'],
    backer: 'EQT / Silver Lake',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'Mehiläinen',
    aliases: ['Mehiläinen', 'Mehiläinen Terveyspalvelut'],
    backer: 'CVC / Investindustrial',
    backerType: 'pe',
    confidence: 'high',
    sector: '(out-of-panel) Primary care',
  },
  {
    name: 'Mindler',
    aliases: ['Mindler', 'Mindler B.V.', 'Mindler Care'],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: 'Specialist medical',
  },
  {
    name: 'MiVet',
    aliases: ['MiVet'],
    backer: 'Columna Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'Mon Véto',
    aliases: ['Mon Véto', 'MonVeto'],
    backer: 'Ardian',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'Mowlam Healthcare',
    aliases: ['Mowlam Healthcare', 'Mowlam'],
    backer: 'Cardinal Capital Group',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Residential elderly care',
  },
  {
    name: 'mydentist',
    aliases: [
      'mydentist',
      'MyDentist',
      'Turnstone EquityCo 1 Ltd',
      'IDH Group',
      'Integrated Dental Holdings',
    ],
    backer: 'Bridgepoint',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Dental',
  },
  {
    name: 'NephroCare',
    aliases: ['NephroCare', 'NephroCare Deutschland GmbH'],
    backer: 'Fresenius Medical Care',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Nesto',
    aliases: ['Nesto', 'Nesto Vets', 'Rigel', 'Orion'],
    backer: 'Verlinvest',
    backerType: 'family',
    confidence: 'medium',
    sector: 'Veterinary',
  },
  {
    name: 'Newmedica',
    aliases: ['Newmedica'],
    backer: 'Specsavers',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'nexeye',
    aliases: ['nexeye', 'Hans Anders', 'eyes + more', 'Direkt Optik'],
    backer: null,
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Optical',
  },
  {
    name: 'Next Fertility Spain',
    aliases: ['Next Fertility Spain', 'Next Clinics', 'IMER Valencia', 'Tahe Fertilidad'],
    backer: 'Next Clinics',
    backerType: 'strategic',
    confidence: 'medium',
    sector: 'Specialist medical',
  },
  {
    name: 'NL Mental Care Group',
    aliases: [
      'NL Mental Care Group',
      'Mentaal Beter',
      'Mentaal Beter Cliënt B.V.',
      'HSK Groep',
      'Vitalmindz',
      'AlleskITs',
      'Opdidakt',
      'De Gezonde Zaak',
    ],
    backer: 'Seven2',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Norlandia',
    aliases: ['Norlandia', 'Norlandia Care', 'Kidsa', 'Aberia'],
    backer: 'Adolfsen Group',
    backerType: 'family',
    confidence: 'medium',
    sector: 'Domiciliary / home care',
  },
  {
    name: 'Oberberg Group',
    aliases: ['Oberberg Group', 'Oberberg Kliniken', 'Somnia Privatkliniken'],
    backer: 'Trilantic Europe',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Oftalvist',
    aliases: ['Oftalvist', 'Clínicas Oftalvist'],
    backer: 'ASISA',
    backerType: 'strategic',
    confidence: 'medium',
    sector: 'Specialist medical',
  },
  {
    name: 'Ophtalys',
    aliases: ['Ophtalys', 'Groupe Ophtalys'],
    backer: 'MBO+',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Optegra',
    aliases: ['Optegra', 'Optegra Eye Hospitals', 'Lexum', 'Iris'],
    backer: 'EssilorLuxottica',
    backerType: 'strategic',
    confidence: 'medium',
    sector: 'Specialist medical',
  },
  {
    name: 'Optic 2000 Group',
    aliases: ['Optic 2000 Group', 'Optic 2000', 'Lissac', 'Audio 2000'],
    backer: null,
    backerType: 'cooperative',
    confidence: 'medium',
    sector: 'Audiology',
  },
  {
    name: 'Optical Center',
    aliases: ['Optical Center'],
    backer: null,
    backerType: 'family',
    confidence: 'low',
    sector: 'Audiology',
  },
  {
    name: 'Optical Express',
    aliases: ['Optical Express', 'ACES', 'Anglia Community Eye Services'],
    backer: 'DCM Optical',
    backerType: 'family',
    confidence: 'medium',
    sector: 'Optical',
  },
  {
    name: 'Optika Anda',
    aliases: ['Optika Anda'],
    backer: 'MidEuropa',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Optical',
  },
  {
    name: 'Oral Care',
    aliases: ['Oral Care', 'Oral Care AB'],
    backer: 'Axcel',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'Orthalis',
    aliases: ['Orthalis', 'Dentalsoft', 'SignalSoft Développement SAS'],
    backer: 'Extens',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'Ortheum',
    aliases: ['Ortheum', 'Ortheum Group', 'Ortheum GmbH'],
    backer: 'Deutsche Private Equity (DPE)',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Ortivity',
    aliases: ['Ortivity', 'Ortivity GmbH'],
    backer: 'Apheon',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Outcomes First Group',
    aliases: [
      'Outcomes First Group',
      'National Fostering Agency',
      'Acorn Care & Education',
      'Options Autism',
      'Hillcrest',
    ],
    backer: 'Apax Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Residential specialist care',
  },
  {
    name: 'oyora',
    aliases: ['oyora', 'oyora GmbH', 'Healthcare Diagnostics Holdings B.V.'],
    backer: 'Healthcare Diagnostics Holdings',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Petits-fils',
    aliases: ['Petits-fils'],
    backer: 'Crédit Agricole Santé & Territoires',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Domiciliary / home care',
  },
  {
    name: 'Pets at Home / Vets4Pets',
    aliases: ['Pets at Home / Vets4Pets', 'Vets4Pets', 'Companion Care', 'Pets at Home Vets'],
    backer: 'Pets at Home Group plc',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'Phoenix Group (UK wholesale)',
    aliases: [
      'Phoenix Group (UK wholesale)',
      'Phoenix Healthcare Distribution',
      'Phoenix Healthcare Distribution Limited',
      'Phoenix Medical Supplies',
      'Phoenix Medical Supplies Limited',
      'PHOENIX Pharmahandel',
    ],
    backer: 'Merckle family (PHOENIX group)',
    backerType: 'family',
    confidence: 'high',
    sector: 'Pharma wholesale',
  },
  {
    name: 'PHV – Der Dialysepartner',
    aliases: ['PHV – Der Dialysepartner', 'Patienten-Heimversorgung Gemeinnützige Stiftung', 'PHV'],
    backer: null,
    backerType: 'foundation',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Point Vision',
    aliases: ['Point Vision', 'Groupe Point Vision', 'Holding des Centres Point Vision SAS'],
    backer: 'Ares Management',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Polaris Community',
    aliases: ['Polaris Community', 'Polaris', 'SWIIS Foster Care', 'Eden', 'Orange Grove'],
    backer: 'CapVest',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Residential specialist care',
  },
  {
    name: 'Policum',
    aliases: ['Policum', 'Policum Berlin MVZ GmbH'],
    backer: 'Palamon Capital Partners',
    backerType: 'pe',
    confidence: 'medium',
    sector: '(out-of-panel) Primary care',
  },
  {
    name: 'PortmanDentex',
    aliases: [
      'PortmanDentex',
      'Portman Dental Care',
      'Portman Healthcare (Group) Ltd',
      'Dentex',
      'Dentex Health',
      'Portman Healthcare Ireland',
    ],
    backer: 'Core Equity Holdings',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Dental',
  },
  {
    name: 'Prevenzione e Salute Holding',
    aliases: [
      'Prevenzione e Salute Holding',
      'Prevenzione e Salute Holding S.r.l.',
      'PSH',
      'Sky Dental Holding',
    ],
    backer: 'Armònia SGR',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Dental',
  },
  {
    name: 'Prime Dental Alliance',
    aliases: ['Prime Dental Alliance', 'Prime Dental Alliance B.V.'],
    backer: 'Certitudo Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'Primo Group',
    aliases: ['Primo Group', 'Centri Dentistici Primo S.p.A.', 'Etica Dentale', 'OasiMedica'],
    backer: 'Aksìa Group',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Dental',
  },
  {
    name: 'Priory Group',
    aliases: [
      'Priory Group',
      'Priory',
      'Priory Healthcare',
      'Priory Adult Care',
      'Life Works',
      'Middleton St George',
    ],
    backer: 'Waterland Private Equity',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Residential specialist care',
  },
  {
    name: 'pro optik',
    aliases: ['pro optik'],
    backer: 'Paragon Partners',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Audiology',
  },
  {
    name: 'PUUR Mondzorg',
    aliases: ['PUUR Mondzorg', 'PUUR Mondzorg B.V.'],
    backer: 'Dentius',
    backerType: 'strategic',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'Quartz Healthcare Germany',
    aliases: [
      'Quartz Healthcare Germany',
      'Quartz Healthcare Germany GmbH',
      'Quartz Healthcare Group S.à r.l.',
      'radprax',
      'radprax Holding GmbH & Co. KG',
      'Dr. Neumaier MVZ',
    ],
    backer: 'InfraVia Capital Partners',
    backerType: 'infra',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Quinta Capital RSA',
    aliases: ['Quinta Capital RSA', 'Quinta Capital SGR', 'PHHL Nursing Homes'],
    backer: 'Quinta Capital SGR',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'Quirónsalud',
    aliases: ['Quirónsalud'],
    backer: 'Fresenius Helios',
    backerType: 'strategic',
    confidence: 'high',
    sector: '(out-of-panel) Hospitals',
  },
  {
    name: 'RAD-x',
    aliases: ['RAD-x'],
    backer: 'Swiss Life AM / Vesper',
    backerType: 'infra',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Ramsay Santé',
    aliases: ['Ramsay Santé', 'Générale de Santé', 'Capio'],
    backer: 'Ramsay Health Care',
    backerType: 'listed',
    confidence: 'high',
    sector: '(out-of-panel) Hospitals',
  },
  {
    name: 'rehaneo',
    aliases: ['rehaneo', 'Reha Vita'],
    backer: 'Gimv',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Physiotherapy & allied health',
  },
  {
    name: 'Reproclinic',
    aliases: ['Reproclinic', 'Ovoclinic Barcelona'],
    backer: 'Axes Health',
    backerType: 'strategic',
    confidence: 'medium',
    sector: 'Specialist medical',
  },
  {
    name: 'Résonance Imagerie',
    aliases: ['Résonance Imagerie', 'Groupe Résonance Imagerie'],
    backer: 'Andera Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'RH Diagnostik & Therapie',
    aliases: [
      'RH Diagnostik & Therapie',
      'RH Diagnostik und Therapie GmbH',
      'Radiologie Holding',
      'Five Arrows Holding VIII S.à r.l.',
    ],
    backer: 'Five Arrows (Rothschild & Co)',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Ribera Salud',
    aliases: ['Ribera Salud', 'HCB Hospitales'],
    backer: 'Vivalto Santé',
    backerType: 'pe',
    confidence: 'medium',
    sector: '(out-of-panel) Hospitals',
  },
  {
    name: 'Rodericks Dental Partners',
    aliases: [
      'Rodericks Dental Partners',
      'Rodericks Dental Partners Ltd',
      'Rodericks Dental',
      'Dental Partners',
    ],
    backer: 'CapVest Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Dental',
  },
  {
    name: 'Salutem Care & Education',
    aliases: [
      'Salutem Care & Education',
      'Salutem',
      'Pathways Care',
      'Modus Care',
      'Clearwater Care',
      'Ambito Care',
      'Ambito Education',
    ],
    backer: null,
    backerType: 'family',
    confidence: 'low',
    sector: 'Residential specialist care',
  },
  {
    name: 'Sanecum Gruppe',
    aliases: ['Sanecum Gruppe', 'Sanecum Gruppe GmbH'],
    backer: 'Adiuva Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Sano Physiotherapy',
    aliases: ['Sano Physiotherapy', 'Back in Action'],
    backer: 'Bestport Private Equity',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Physiotherapy & allied health',
  },
  {
    name: 'Sanoptis',
    aliases: ['Sanoptis', 'Sanoptis AG'],
    backer: 'Groupe Bruxelles Lambert',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Schoonenberg HoorComfort',
    aliases: ['Schoonenberg HoorComfort', 'Schoonenberg'],
    backer: 'Sonova',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Audiology',
  },
  {
    name: 'Sereni Orizzonti',
    aliases: ['Sereni Orizzonti'],
    backer: null,
    backerType: 'family',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'Sevetys',
    aliases: ['Sevetys', 'VetUnion', 'Sirius Vet'],
    backer: 'Eurazeo',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'Signature Senior Lifestyle',
    aliases: ['Signature Senior Lifestyle', 'Signature'],
    backer: 'Welltower',
    backerType: 'reit',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'Simago',
    aliases: ['Simago', 'Simago SAS', 'Simago Group', 'Centro Radiologico dei Laghi'],
    backer: 'Ardian',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Skinclinic',
    aliases: ['Skinclinic'],
    backer: 'Nexxus Iberia',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Specialist medical',
  },
  {
    name: 'Smile Eyes',
    aliases: ['Smile Eyes', 'Smile Eyes TopCo GmbH', 'Panorama Fachklinik GmbH'],
    backer: 'Trilantic Europe',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Smile Tandvård',
    aliases: ['Smile Tandvård', 'Smile.se', 'Colosseum Smile Group'],
    backer: 'Jacobs Holding',
    backerType: 'family',
    confidence: 'high',
    sector: 'Dental',
  },
  {
    name: 'Sonic Healthcare (EU labs)',
    aliases: [
      'Sonic Healthcare (EU labs)',
      'The Doctors Laboratory',
      'TDL',
      'Health Services Laboratories',
      'HSL',
      'LADR',
      'Dr. Kramer & Kollegen',
      'Bioscientia',
    ],
    backer: 'Sonic Healthcare',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Sonic Healthcare Germany (brands)',
    aliases: [
      'Sonic Healthcare Germany (brands)',
      'Bioscientia Healthcare GmbH',
      'Bioscientia',
      'Diagnosticum',
      'Labor Dr. von Froreich',
      'MLD Düsseldorf',
      'Labor im Sommershof',
      'Laborgruppe Augsburg',
      'Labor Staber',
    ],
    backer: 'Sonic Healthcare',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Sonova (retail)',
    aliases: [
      'Sonova (retail)',
      'AudioNova',
      'Geers',
      'Schoonenberg',
      'Minisom',
      'Boots Hearingcare',
    ],
    backer: 'Sonova Holding (listed)',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Audiology',
  },
  {
    name: 'Source BioScience',
    aliases: [
      'Source BioScience',
      'Source LDPath',
      'Source Genomics',
      'Cambridge Clinical Laboratories',
    ],
    backer: 'Qualitas Funds',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Synlab France',
    aliases: ['Synlab France', 'Synlab Biofrance', 'Synlab Provence', 'Synlab Nouvelle-Aquitaine'],
    backer: 'Cinven',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'SYNLAB Group',
    aliases: [
      'SYNLAB Group',
      'SYNLAB',
      'SYNLAB-Labco',
      'Labco',
      'Bioscientia',
      'SYNLAB Italia',
      'SYNLAB Belgium',
    ],
    backer: 'Cinven',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Synsam',
    aliases: ['Synsam', 'Profil Optik', 'Smarteyes'],
    backer: 'Synsam AB (listed)',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Optical',
  },
  {
    name: 'Tandarts Today',
    aliases: ['Tandarts Today', 'Tandarts Today B.V.'],
    backer: 'Gilde Healthcare',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'The Fertility Partnership',
    aliases: [
      'The Fertility Partnership',
      'TFP',
      'VivaNeo',
      'Kinderwunschzentrum',
      'Wijnland',
      'TFP Fertility',
    ],
    backer: 'Impilo',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Therapie Clinic',
    aliases: ['Therapie Clinic', 'Thérapie Clinic', 'Thérapie Fertility'],
    backer: null,
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Specialist medical',
  },
  {
    name: 'Tierarzt Plus Partner',
    aliases: ['Tierarzt Plus Partner', 'Tierarzt Plus', 'TPP'],
    backer: 'Inflexion',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'Together Dental',
    aliases: ['Together Dental', 'Dental Care Group'],
    backer: 'G Square Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'TopMondzorg (NL dental brands)',
    aliases: [
      'TopMondzorg (NL dental brands)',
      'Top Mondzorg B.V.',
      'Proclin',
      'MondzorgPlus',
      'Omnios',
      'MKA Groep',
      'Gerident',
      'Excent Tandtechniek',
    ],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: 'Dental',
  },
  {
    name: 'TopzorgGroep',
    aliases: ['TopzorgGroep', 'FysioHolland'],
    backer: 'Capital A',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Physiotherapy & allied health',
  },
  {
    name: 'Trinity Homecare',
    aliases: ['Trinity Homecare'],
    backer: 'Limerston Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Domiciliary / home care',
  },
  {
    name: 'UNAVETS Group',
    aliases: ['UNAVETS Group', 'UNAVETS', 'ONEVET', 'Leçavet', 'Freixiero', 'easyvet'],
    backer: 'Oaktree Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'Unilabs',
    aliases: ['Unilabs', 'Unilabs Røntgen', 'Unilabs España', 'Saltro'],
    backer: 'A.P. Moller Holding',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Unimed Group',
    aliases: ['Unimed Group', 'Unimed Group S.r.l.'],
    backer: 'White Italia',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Diagnostics & labs',
  },
  {
    name: 'Universal Clinics (Genera)',
    aliases: ['Universal Clinics (Genera)', 'Universal Clinics', 'Genera Health Care'],
    backer: 'Investindustrial',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Univet',
    aliases: ['Univet', 'Univet Réseau'],
    backer: 'InfraVia Capital Partners',
    backerType: 'infra',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'VALEARA',
    aliases: [
      'VALEARA',
      'VALEARA Bottrop GmbH',
      'GNS Gesellschaft für Neurologie und seelische Gesundheit mbH',
    ],
    backer: 'Genui',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Van Boxtel Hoorwinkels',
    aliases: ['Van Boxtel Hoorwinkels'],
    backer: 'Demant',
    backerType: 'strategic',
    confidence: 'high',
    sector: 'Audiology',
  },
  {
    name: 'Veonet Group',
    aliases: [
      'Veonet Group',
      'Veonet',
      'Ober Scharrer Gruppe',
      'OSG',
      'SpaMedica',
      'Eyescan',
      'Vista Augenklinik',
      'Miranza',
      'Optimax',
      'Ultralase',
    ],
    backer: "PAI Partners / Ontario Teachers'",
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Vetopia',
    aliases: ['Vetopia', 'VetThing'],
    backer: 'Axcel',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Veterinary',
  },
  {
    name: 'VetPartners',
    aliases: [
      'VetPartners',
      'VetPartners Italia',
      'VetPartners Ireland',
      'Goddard',
      'Clyde',
      'Rossdales',
      'Westpoint',
    ],
    backer: 'BC Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'VetPartners España',
    aliases: ['VetPartners España', 'VetPartners España SL', 'Vetpartners Clínicas España SL'],
    backer: 'BC Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'Vetsia',
    aliases: ['Vetsia', 'Hospital Veterinario Vetsia'],
    backer: 'Collins Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Veterinary',
  },
  {
    name: 'Viamed Salud',
    aliases: ['Viamed Salud'],
    backer: 'Macquarie Asset Management',
    backerType: 'infra',
    confidence: 'high',
    sector: '(out-of-panel) Hospitals',
  },
  {
    name: 'Vida Fertility',
    aliases: ['Vida Fertility', 'Vida Fertility Institute SL'],
    backer: 'Grupo Recoletas',
    backerType: 'family',
    confidence: 'medium',
    sector: 'Specialist medical',
  },
  {
    name: 'Village Vets',
    aliases: ['Village Vets'],
    backer: 'Inflexion',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'Vimian Group',
    aliases: ['Vimian Group', 'Vimian', 'VetFamily', 'VetPlan', 'Indical', 'Movora'],
    backer: 'Fidelio Capital (anchor)',
    backerType: 'listed',
    confidence: 'high',
    sector: 'Veterinary',
  },
  {
    name: 'Virtue Integrated Elder Care',
    aliases: ['Virtue Integrated Elder Care', 'Virtue', 'Beechfield'],
    backer: 'Emera Group',
    backerType: 'family',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'Virtus Health',
    aliases: ['Virtus Health', 'Sims IVF'],
    backer: 'CapVest Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Vision Group',
    aliases: ['Vision Group', 'VistaSì'],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: 'Optical',
  },
  {
    name: 'Vista Vision',
    aliases: ['Vista Vision'],
    backer: 'Ardian',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'Vitalia Home',
    aliases: [
      'Vitalia Home',
      'Vitalia',
      'Vitalia Plus S.A.',
      'ES: Vitalia Plus S.A.',
      'ES: Vitalia',
    ],
    backer: 'StepStone + Greykite',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Residential elderly care',
  },
  {
    name: 'Vitalliance',
    aliases: ['Vitalliance'],
    backer: 'Naxicap / Ardian',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Domiciliary / home care',
  },
  {
    name: 'Vítaly (Cualtis + Preving)',
    aliases: ['Vítaly (Cualtis + Preving)', 'Vítaly', 'Grupo Preving', 'Cualtis'],
    backer: 'Artá Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: '(out-of-panel) Occupational health',
  },
  {
    name: 'Vitanas',
    aliases: ['Vitanas', 'Pflegen & Wohnen Hamburg'],
    backer: 'Domidep',
    backerType: 'infra',
    confidence: 'medium',
    sector: 'Residential elderly care',
  },
  {
    name: 'Vithas',
    aliases: ['Vithas', 'Adeslas', 'NISA'],
    backer: 'Goodgrower',
    backerType: 'family',
    confidence: 'medium',
    sector: '(out-of-panel) Hospitals',
  },
  {
    name: 'Vivalto Santé',
    aliases: ['Vivalto Santé'],
    backer: 'IK Partners / Vivalto Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: '(out-of-panel) Hospitals',
  },
  {
    name: 'Vivalto Vie',
    aliases: ['Vivalto Vie', 'Vivalto Vie Holding SAS'],
    backer: 'Hayfin',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Residential elderly care',
  },
  {
    name: 'Vivanta',
    aliases: ['Vivanta', 'Vivantadental'],
    backer: 'Portobello Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Dental',
  },
  {
    name: 'VivaSon',
    aliases: ['VivaSon', 'VivaSon Alpha SAS'],
    backer: 'Indigo Capital',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Audiology',
  },
  {
    name: 'Voyage Care',
    aliases: ['Voyage Care', 'Voyage (DCA)'],
    backer: 'Wren House Infrastructure',
    backerType: 'infra',
    confidence: 'high',
    sector: 'Residential specialist care',
  },
  {
    name: 'Vulpia',
    aliases: ['Vulpia', 'Vulpia Care Group'],
    backer: null,
    backerType: 'family',
    confidence: 'low',
    sector: 'Domiciliary / home care',
  },
  {
    name: 'Witherslack Group',
    aliases: ['Witherslack Group', 'Witherslack'],
    backer: null,
    backerType: 'strategic',
    confidence: 'low',
    sector: 'Residential specialist care',
  },
  {
    name: 'Wm Clinics Group',
    aliases: [
      'Wm Clinics Group',
      'Wm Clinics',
      'Dorsia',
      'Origen',
      'Tu Derma',
      'IML',
      'Instituto Médico Láser',
      'Policlínico de Barcelona',
    ],
    backer: 'Peninsula Capital',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Specialist medical',
  },
  {
    name: 'WS Audiology',
    aliases: ['WS Audiology', 'WSA', 'Widex', 'Signia', 'Sivantos', 'hear.com', 'Audibene'],
    backer: 'EQT / Santo Holding',
    backerType: 'pe',
    confidence: 'medium',
    sector: 'Audiology',
  },
  {
    name: 'zahneins',
    aliases: ['zahneins', 'zahneins GmbH', 'KonfiDents', 'KonfiDor'],
    backer: 'PAI Partners',
    backerType: 'pe',
    confidence: 'high',
    sector: 'Dental',
  },
];
